import { AlertTriangle, CheckCircle2, Info, Sparkles, X } from 'lucide-react';
import { cn } from '../lib/utils';
import { useAppStore, type Toast } from '../store/useAppStore';

const ICONS: Record<Toast['kind'], typeof Info> = {
  info: Info,
  success: CheckCircle2,
  error: AlertTriangle,
  remote: Sparkles,
};

const STYLES: Record<Toast['kind'], string> = {
  info: 'border-[var(--color-line)] text-[var(--color-ink)]',
  success: 'border-[var(--color-fresh)]/50 text-[var(--color-ink)]',
  error: 'border-[var(--color-moved)]/60 text-[var(--color-ink)]',
  remote: 'border-[var(--color-brand)]/60 text-[var(--color-ink)]',
};

const ICON_STYLES: Record<Toast['kind'], string> = {
  info: 'text-[var(--color-ink-3)]',
  success: 'text-[var(--color-fresh)]',
  error: 'text-[var(--color-moved)]',
  remote: 'text-[var(--color-brand)]',
};

export function Toasts() {
  const toasts = useAppStore((s) => s.toasts);
  const dismiss = useAppStore((s) => s.dismissToast);

  if (toasts.length === 0) return null;

  return (
    <div className="pointer-events-none fixed top-16 right-4 z-50 flex w-[min(330px,calc(100vw-2rem))] flex-col gap-2">
      {toasts.slice(-4).map((toast) => {
        const Icon = ICONS[toast.kind];
        return (
          <div
            key={toast.id}
            className={cn(
              'card-in pointer-events-auto flex items-start gap-2 rounded-xl border bg-[var(--color-surface)]/97 px-3 py-2.5',
              'shadow-[var(--shadow-panel)] backdrop-blur',
              STYLES[toast.kind],
            )}
          >
            <Icon size={15} className={cn('mt-px shrink-0', ICON_STYLES[toast.kind])} />
            <div className="flex-1">
              <p className="text-[12.5px] leading-snug">
                {toast.kind === 'remote' ? <span className="font-semibold">From Claude: </span> : null}
                {toast.message}
              </p>
              {/* A plain anchor, for the reason `Toast.href` gives. */}
              {toast.href ? (
                <a
                  href={toast.href}
                  target="_blank"
                  rel="noreferrer"
                  className="mt-0.5 inline-block text-[12px] font-semibold text-[var(--color-brand)] hover:underline"
                >
                  Open it
                </a>
              ) : null}
            </div>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={() => dismiss(toast.id)}
              className="-mt-0.5 -mr-1 rounded p-1 text-[var(--color-ink-3)] transition-colors hover:text-[var(--color-ink)]"
            >
              <X size={13} />
            </button>
          </div>
        );
      })}
    </div>
  );
}
